import { sqExploreFolderModalStore, sqExploreWorkbookModalStore } from '@/core/core.stores';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { cancelGroup } from '@/requests/pendingRequests.utilities';
import { sqFoldersApi } from '@seeqdev/api/endpoint/FoldersApi';
import type { Folder, SearchParams } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { GetFoldersRootEnum as RootEnum } from '@seeqdev/api/endpoint/FoldersApi';

export function setInitialFolderId(initialFolderId?: string) {
  sqExploreFolderModalStore.actions.setInitialFolderId(initialFolderId);
}

/**
 * Sets the workbook ID
 *
 * @param workbookId - a workbook ID
 * @param workbookType - type of the workbook (e.g., Analysis, Vantage)
 */
export function setWorkbookId(workbookId?: string, workbookType = 'Analysis') {
  sqExploreWorkbookModalStore.actions.setWorkbookId(workbookId, workbookType);
}

/**
 * Sets the sort field that controls the sorting of the workbooks and toggles the direction if the field
 * was already set to the specified one.
 *
 * @param sortProperty - One of WORKBOOKS_SORT_FIELDS
 * @param ascending - True for asc, false for desc
 * @param forceSortDirection=false - True if the given sort direction must not be flipped when sorting
 *   on the currently sorted property (normally used to toggle sort direction)
 */
export function setSort(sortProperty: string, ascending: boolean, forceSortDirection = false) {
  const sortAscending =
    !forceSortDirection && sortProperty === sqExploreWorkbookModalStore.getState().sortProperty
      ? !ascending
      : ascending;
  sqExploreWorkbookModalStore.actions.setSort({ sortProperty, sortAscending });
}

/**
 * Clears the data for the currently loaded seeq content image.
 */
export function clear() {
  sqExploreWorkbookModalStore.actions.clear();
}

export function setTableFilter(tableFilter: SearchItemLocation) {
  sqExploreWorkbookModalStore.actions.setTableFilter(tableFilter);
}

export function setSearchParams(field: keyof SearchParams | string, value: unknown) {
  sqExploreWorkbookModalStore.actions.setSearchParam(field as keyof SearchParams, value);
}

export function setPageSizeForTable({ size }: { size: number }) {
  sqExploreWorkbookModalStore.actions.setPageSize(size);
}

export function setPageNumberAndGo(page: number, loadTable: (...args: any[]) => any) {
  sqExploreWorkbookModalStore.actions.setPageNumber(page);
  loadTable();
}

export function setFolder(folder?: Folder) {
  sqExploreWorkbookModalStore.actions.setFolder(folder);
}

export function setShowResults(showResults: boolean) {
  sqExploreWorkbookModalStore.actions.setShowResults(showResults);
}

export function setTableRoot(root?: string) {
  sqExploreWorkbookModalStore.actions.setTableRoot(root as RootEnum | undefined);
}

export function loadItems() {
  const state = sqExploreWorkbookModalStore.getState();
  const limit = state.pageSize;
  const offset = (state.pageNumber - 1) * limit;
  const sortDirection = state.sortAscending ? 'asc' : 'desc';
  const sortOrder = `${state.sortProperty} ${sortDirection}`;
  const root = state.tableRoot;
  const params = {
    ...state.searchParams,
    limit,
    offset,
    sortOrder,
    root,
  };

  const cancellationGroup = 'exploreWorkbookModalLoadItems';
  sqExploreWorkbookModalStore.actions.setLoading(true);
  cancelGroup(cancellationGroup);
  return sqFoldersApi
    .getFolders(params, { cancellationGroup })
    .then((response) => {
      sqExploreWorkbookModalStore.actions.setItems(response.data?.content ?? []);
      sqExploreWorkbookModalStore.actions.setItemTotalForTable(response.data?.totalResults ?? 0);
    })
    .finally(() => sqExploreWorkbookModalStore.actions.setLoading(false));
}

export function getRootAndUpdateFilter(folderId: string | undefined) {
  if (folderId) {
    return sqFoldersApi.getAncestors({ folderId }).then((folderTree) => {
      const itemFolderRoot = folderTree.data.id;
      const rootsThatMapToFilters: string[] = [
        SEARCH_ITEM_LOCATIONS.MY_FOLDER,
        SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC,
        SEARCH_ITEM_LOCATIONS.CORPORATE,
      ];
      if (itemFolderRoot && rootsThatMapToFilters.includes(itemFolderRoot)) {
        setTableFilter(itemFolderRoot as SearchItemLocation);
        setSearchParams('filter', itemFolderRoot);
      }
    });
  } else {
    return Promise.resolve({});
  }
}
